import i18n from '@/i18n';
import { localeTag } from '@/i18n/format';
/**
 * MiniPDF — Lightweight PDF generator (no external deps).
 *
 * Supports:
 *  - A4 landscape/portrait pages
 *  - Embedded JPEG/PNG images
 *  - Text (title, subtitle, body)
 *  - Page headers/footers
 *  - Multi-page documents
 *
 * Produces a valid PDF 1.4 binary that opens in any viewer.
 */

type Orientation = 'portrait' | 'landscape';

interface PageDef {
  orientation: Orientation;
  objects: PageObject[];
}

type PageObject =
  | { kind: 'image'; data: string; x: number; y: number; w: number; h: number; format: 'JPEG' | 'PNG' }
  | { kind: 'text'; text: string; x: number; y: number; fontSize: number; color: [number, number, number]; align?: 'left' | 'center' | 'right'; bold?: boolean };

const A4_W = 841.89; // landscape width in pt
const A4_H = 595.28; // landscape height in pt

export class MiniPDF {
  private pages: PageDef[] = [];
  private headerText = '';

  setHeader(text: string) {
    this.headerText = text;
  }

  addPage(orientation: Orientation = 'landscape'): number {
    this.pages.push({ orientation, objects: [] });
    return this.pages.length - 1;
  }

  addImage(page: number, dataUrl: string, x: number, y: number, w: number, h: number) {
    const format = dataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
    this.pages[page].objects.push({ kind: 'image', data: dataUrl, x, y, w, h, format });
  }

  addText(page: number, text: string, x: number, y: number, opts?: { fontSize?: number; color?: [number, number, number]; align?: 'left' | 'center' | 'right'; bold?: boolean }) {
    this.pages[page].objects.push({
      kind: 'text', text, x, y,
      fontSize: opts?.fontSize ?? 12,
      color: opts?.color ?? [0, 0, 0],
      align: opts?.align ?? 'left',
      bold: opts?.bold ?? false,
    });
  }

  /** Get page dimensions in pt */
  getPageSize(page: number): { w: number; h: number } {
    const p = this.pages[page];
    return p.orientation === 'landscape' ? { w: A4_W, h: A4_H } : { w: A4_H, h: A4_W };
  }

  /**
   * Build PDF and trigger download.
   * Uses a canvas-based approach to render each page as a full-page JPEG image,
   * which is far simpler than emitting raw PDF drawing commands and handles
   * all text/image compositing via Canvas2D.
   */
  async download(filename: string) {
    // We'll render each page to a canvas, convert to JPEG, then embed in a minimal PDF
    const imagePages: { jpeg: Uint8Array; w: number; h: number }[] = [];

    for (let pi = 0; pi < this.pages.length; pi++) {
      const page = this.pages[pi];
      const { w: pw, h: ph } = this.getPageSize(pi);
      const scale = 2; // render at 2x for quality
      const canvas = document.createElement('canvas');
      canvas.width = pw * scale;
      canvas.height = ph * scale;
      const ctx = canvas.getContext('2d')!;
      ctx.scale(scale, scale);

      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pw, ph);

      // Header
      if (this.headerText) {
        ctx.fillStyle = '#1c354c';
        ctx.fillRect(0, 0, pw, 36);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 14px Helvetica, Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(this.headerText, pw / 2, 24);
      }

      // Footer
      ctx.fillStyle = '#e5e7eb';
      ctx.fillRect(0, ph - 24, pw, 24);
      ctx.fillStyle = '#6b7280';
      ctx.font = '9px Helvetica, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(i18n.t('report.page_of', { page: pi + 1, total: this.pages.length }), pw / 2, ph - 9);
      ctx.textAlign = 'right';
      ctx.fillText(new Date().toLocaleDateString(localeTag()), pw - 12, ph - 9);

      // Render objects
      for (const obj of page.objects) {
        if (obj.kind === 'text') {
          ctx.fillStyle = `rgb(${obj.color[0]},${obj.color[1]},${obj.color[2]})`;
          ctx.font = `${obj.bold ? 'bold ' : ''}${obj.fontSize}px Helvetica, Arial, sans-serif`;
          ctx.textAlign = obj.align || 'left';
          ctx.fillText(obj.text, obj.x, obj.y);
        } else if (obj.kind === 'image') {
          try {
            const img = await loadImage(obj.data);
            ctx.drawImage(img, obj.x, obj.y, obj.w, obj.h);
          } catch { /* skip broken images */ }
        }
      }

      // Convert canvas to JPEG bytes
      const blob = await new Promise<Blob>((res) => canvas.toBlob((b) => res(b!), 'image/jpeg', 0.92));
      const buf = await blob.arrayBuffer();
      imagePages.push({ jpeg: new Uint8Array(buf), w: pw, h: ph });
    }

    // Build minimal PDF with one JPEG per page
    const pdfBytes = buildPdfFromJpegs(imagePages);
    const pdfBlob = new Blob([pdfBytes.buffer as ArrayBuffer], { type: 'application/pdf' });
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

/* ── Helpers ── */

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
}

/** Build a valid PDF 1.4 file containing one full-page JPEG image per page. */
function buildPdfFromJpegs(pages: { jpeg: Uint8Array; w: number; h: number }[]): Uint8Array {
  const enc = new TextEncoder();
  const parts: (string | Uint8Array)[] = [];
  const offsets: number[] = [];
  let pos = 0;

  const write = (s: string) => { parts.push(s); pos += enc.encode(s).length; };
  const writeBytes = (b: Uint8Array) => { parts.push(b); pos += b.length; };
  const markObj = () => { offsets.push(pos); };

  write('%PDF-1.4\n%\xE2\xE3\xCF\xD3\n');

  // Object 1: Catalog
  markObj();
  write('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');

  // Object 2: Pages (placeholder, we'll write it after we know page objects)
  // We need to know all page object IDs first
  // Structure: obj 1 = catalog, obj 2 = pages, then per page: page obj + image XObject
  // So page N has: pageObj = 3 + N*2, imageObj = 4 + N*2

  const pageObjIds = pages.map((_, i) => 3 + i * 2);
  const imgObjIds = pages.map((_, i) => 4 + i * 2);
  markObj();
  const kidsStr = pageObjIds.map((id) => `${id} 0 R`).join(' ');
  write(`2 0 obj\n<< /Type /Pages /Kids [${kidsStr}] /Count ${pages.length} >>\nendobj\n`);

  for (let i = 0; i < pages.length; i++) {
    const { jpeg, w, h } = pages[i];
    const pageId = pageObjIds[i];
    const imgId = imgObjIds[i];

    // Page object
    markObj();
    write(`${pageId} 0 obj\n`);
    write(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${w.toFixed(2)} ${h.toFixed(2)}] `);
    write(`/Contents ${imgId + 1} 0 R /Resources << /XObject << /Im0 ${imgId} 0 R >> >> >>\n`);
    write('endobj\n');

    // Image XObject
    markObj();
    write(`${imgId} 0 obj\n`);
    write(`<< /Type /XObject /Subtype /Image /Width ${Math.round(w * 2)} /Height ${Math.round(h * 2)} `);
    write(`/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\n`);
    write('stream\n');
    writeBytes(jpeg);
    write('\nendstream\nendobj\n');

    // Content stream (draw image full page)
    // We need an extra object for the content stream
    // Adjust: we need totalObjs to account for content streams too
  }

  // Actually, let me restructure. Each page needs 3 objects: page, image, content stream.
  // Let me redo this properly.

  // Reset
  parts.length = 0;
  offsets.length = 0;
  pos = 0;

  write('%PDF-1.4\n%\xE2\xE3\xCF\xD3\n');

  // obj 1: catalog
  markObj();
  write('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');

  // obj 2: pages — refs computed below
  // Per page we need 3 objects: page, image, content
  // pageObj = 3 + i*3, imgObj = 4 + i*3, contObj = 5 + i*3
  const pgIds = pages.map((_, i) => 3 + i * 3);
  const imIds = pages.map((_, i) => 4 + i * 3);
  const ctIds = pages.map((_, i) => 5 + i * 3);

  markObj();
  const kids = pgIds.map((id) => `${id} 0 R`).join(' ');
  write(`2 0 obj\n<< /Type /Pages /Kids [${kids}] /Count ${pages.length} >>\nendobj\n`);

  for (let i = 0; i < pages.length; i++) {
    const { jpeg, w, h } = pages[i];

    // Content stream: scale and draw image
    const contentStr = `q\n${w.toFixed(2)} 0 0 ${h.toFixed(2)} 0 0 cm\n/Im0 Do\nQ\n`;
    const contentBytes = enc.encode(contentStr);

    // Page
    markObj();
    write(`${pgIds[i]} 0 obj\n`);
    write(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${w.toFixed(2)} ${h.toFixed(2)}] `);
    write(`/Contents ${ctIds[i]} 0 R /Resources << /XObject << /Im0 ${imIds[i]} 0 R >> >> >>\n`);
    write('endobj\n');

    // Image XObject
    markObj();
    write(`${imIds[i]} 0 obj\n`);
    write(`<< /Type /XObject /Subtype /Image /Width ${Math.round(w * 2)} /Height ${Math.round(h * 2)} `);
    write(`/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\n`);
    write('stream\n');
    writeBytes(jpeg);
    write('\nendstream\nendobj\n');

    // Content stream
    markObj();
    write(`${ctIds[i]} 0 obj\n`);
    write(`<< /Length ${contentBytes.length} >>\n`);
    write('stream\n');
    writeBytes(contentBytes);
    write('\nendstream\nendobj\n');
  }

  // Cross-reference table
  const xrefPos = pos;
  const nObjs = offsets.length + 1; // +1 for free entry
  write(`xref\n0 ${nObjs}\n`);
  write('0000000000 65535 f \n');
  for (const off of offsets) {
    write(`${String(off).padStart(10, '0')} 00000 n \n`);
  }

  write('trailer\n');
  write(`<< /Size ${nObjs} /Root 1 0 R >>\n`);
  write('startxref\n');
  write(`${xrefPos}\n`);
  write('%%EOF\n');

  // Concatenate all parts
  let totalLen = 0;
  for (const p of parts) {
    totalLen += typeof p === 'string' ? enc.encode(p).length : p.length;
  }
  const result = new Uint8Array(totalLen);
  let off = 0;
  for (const p of parts) {
    const bytes = typeof p === 'string' ? enc.encode(p) : p;
    result.set(bytes, off);
    off += bytes.length;
  }
  return result;
}
